import { isDate, isObject } from 'lodash-es';
import type { TrackProps, PageProps } from '../context';
import type { TrackFunctionMap, PageFunctionMap, TelemetryObjectArgs } from './types';

export const wrapActionsInAnalytics = <A extends TrackFunctionMap, C = undefined>(
  track: (payload: TrackProps) => void,
  actions: A,
  context?: C,
  enrichedExtraInfo?: Record<string, unknown>,
) => {
  return Object.keys(actions).reduce((acc: A, curr: keyof A) => {
    return {
      ...acc,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [curr]: (...args: any[]) => {
        const payload = actions[curr](...args);
        track({ ...payload, extraInfo: { ...payload.extraInfo, ...enrichedExtraInfo }, context });
      },
    };
  }, {} as A);
};

type PagesEnter<P extends PageFunctionMap> = Readonly<{
  [Key in keyof P]: Readonly<{
    enter: P[Key];
  }>;
}>;

export const wrapPagesInAnalytics = <P extends PageFunctionMap, C>(page: (payload: PageProps) => void, pages?: P, context?: C, enrichedExtraInfo?: Record<string, unknown>) => {
  if (pages) {
    return Object.keys(pages).reduce((acc: PagesEnter<P>, curr) => {
      return {
        ...acc,
        [curr]: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          enter: (...args: any[]) => {
            const payload = pages[curr](...args);
            page({ ...payload, extraPageInfo: { ...payload.extraPageInfo, ...enrichedExtraInfo }, context, action: 'enter' });
          },
        },
      };
    }, {} as PagesEnter<P>);
  }
  return {} as PagesEnter<P>;
};

// takes an Object of arbitrary depth values and produces a flat object with truncated paths.
//   all keys are sorted, and in the case of overlap, the first key wins
//   best explanation is to look at the Test Suite.
//   "why?"  because Postgres has a max identifier length of 63 characters.
//   the auto-collapse of nested keys can result in a column name that Segment can't create.
type RewriteTuples = Array<[string, unknown]>;
type RewriteRecord = Record<string, unknown>;
type RewriteNode = RewriteRecord | Array<RewriteNode>;
export function rewriteWithCompactedKeys(inputRaw: unknown, options: { maxKeyLength: number } = { maxKeyLength: 50 }): RewriteRecord {
  // flattened tuples, with full length paths
  const input = inputRaw as RewriteNode;
  const output = _traverseRewriteWithCompactedKeys(input, []);

  // predictable key ordering
  const sorted = output.sort((a, b) => a[0].localeCompare(b[0]));
  const recorded = sorted.reduce((acc, [path, value]) => {
    // key compaction => potential loss of resolution
    const key = path.slice(0, options.maxKeyLength);
    if (!Object.hasOwn(acc, key)) {
      acc[key] = value;
    }
    return acc;
  }, {} as RewriteRecord);

  return recorded;
}
function _isRewriteNode(value: unknown): value is RewriteNode {
  // supports Array indexes as keys
  return isObject(value) || Array.isArray(value);
}
function _traverseRewriteWithCompactedKeys(input: RewriteNode, parents: string[]): RewriteTuples {
  let tuples: RewriteTuples = [];
  for (const [key, value] of Object.entries(input)) {
    const newParents = [...parents, key];
    if (isDate(value)) {
      tuples.push([newParents.join('.'), value.toISOString()]);
    } else if (_isRewriteNode(value)) {
      tuples = [...tuples, ..._traverseRewriteWithCompactedKeys(value, newParents)];
    } else {
      tuples.push([newParents.join('.'), value]);
    }
  }
  return tuples;
}

export const createTelemetryObject = <A extends TrackFunctionMap, P extends PageFunctionMap>(config: TelemetryObjectArgs<A, P>) => config;
